/**
 * Fallback storage for the `body` refresh transport.
 *
 * The API decides per-login how the refresh token reaches the client
 * (controllers/authController.js → resolveTransport):
 *
 *   cookie — the browser is same-site with the API, so the token rides in the
 *            HttpOnly `bsn_rt` cookie. Nothing here is used; this is the path
 *            we want in production.
 *   body   — the API judged us cross-site (AUTH_COOKIE_DOMAIN unset or not
 *            matching this host, an IP/preview domain, no Origin at all), so a
 *            cookie would be set and then never sent back. The token comes
 *            down in the JSON body instead and the CLIENT has to hold it.
 *
 * Until this module existed the web app implemented only the cookie half, so
 * anything that resolved to `body` silently had no way to refresh: the session
 * simply died when the 15-minute access token expired.
 *
 * SECURITY. localStorage is readable by any script on the page, so a stored
 * refresh token is strictly weaker than the HttpOnly cookie — this is a
 * compatibility fallback, not the target state. Fixing the deployment
 * (AUTH_COOKIE_DOMAIN=.yourdomain.com on the API, HTTPS, app and API on the
 * same registrable domain) puts every browser back on the cookie path, and
 * then nothing is ever written here.
 */

const KEY = 'bsn-refresh-token';

/** Shape of the session block every token-issuing endpoint returns. */
export interface SessionPayload {
  accessToken?: string;
  refreshToken?: string | null;
  refreshTransport?: 'cookie' | 'body' | null;
  user?: unknown;
}

let warned = false;

function warnOnce() {
  if (warned || process.env.NODE_ENV === 'production') return;
  warned = true;
  console.warn(
    '[auth] API returned the refresh token in the response body — this browser is '
    + 'not same-site with the API. Falling back to localStorage. Set AUTH_COOKIE_DOMAIN '
    + 'on the API (and serve the app from that domain over HTTPS) to use the HttpOnly cookie.',
  );
}

/**
 * Record the token from a session-issuing response. Safe to call with any
 * response body: `body` stores, `cookie` retires an obsolete stored token, and
 * a legacy or non-session response is a no-op.
 */
export function adoptSession(session: SessionPayload | null | undefined): void {
  if (typeof window === 'undefined' || !session) return;
  if (session.refreshTransport === 'cookie') {
    // The deployment moved onto the cookie transport (AUTH_COOKIE_DOMAIN now
    // set, or the app moved onto the API's domain). A token left over from the
    // body era would be sent alongside the cookie and rejected as
    // REFRESH_AMBIGUOUS, so retire it the moment we see a cookie session.
    clearStoredRefreshToken();
    return;
  }
  if (session.refreshTransport !== 'body') return;
  if (typeof session.refreshToken !== 'string' || !session.refreshToken) return;
  warnOnce();
  try {
    localStorage.setItem(KEY, session.refreshToken);
  } catch { /* private mode / quota — the session just won't survive expiry */ }
}

/** The stored token, or '' when this browser is on the cookie transport. */
export function storedRefreshToken(): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(KEY) || '';
  } catch {
    return '';
  }
}

export function clearStoredRefreshToken(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(KEY);
  } catch { /* nothing we can do; the token expires server-side regardless */ }
}

/**
 * Body-transport counterpart to the `bsn_sess` cookie: tells a cold tab that a
 * session probably exists here and it should try /auth/refresh before deciding
 * the user is logged out.
 */
export function hasStoredRefreshToken(): boolean {
  return storedRefreshToken() !== '';
}
